import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { porTier, TOPO, LogoWrap, logoCls } from "../data/parceiros";

const TierLabel = ({ children, testid }) => (
    <p className="font-caps text-[10px] tracking-[0.32em] uppercase text-[#c9a24b]/70 text-center" data-testid={testid}>{children}</p>
);

export const Patrocinadores = () => {
    const { t } = useLanguage();
    const tiers = t.assoc.parceiros.tiersPlural;
    return (
        <section className="py-8 sm:py-10" data-testid="patrocinadores-section">
            <div className="mx-auto max-w-[1500px] px-5 sm:px-10">
                <Reveal>
                    <div className="flex items-center justify-center gap-4 sm:gap-5">
                        <span className="hidden sm:block h-px flex-1 max-w-56 bg-gradient-to-l from-[#c9a24b]/60 to-transparent" />
                        <span className="hidden sm:block w-1.5 h-1.5 rotate-45 border border-[#c9a24b]" />
                        <h2 className="font-caps gold-text text-lg sm:text-xl tracking-[0.2em] text-center" data-testid="patrocinadores-title">
                            {t.patrocinadores.titulo}
                        </h2>
                        <span className="hidden sm:block w-1.5 h-1.5 rotate-45 border border-[#c9a24b]" />
                        <span className="hidden sm:block h-px flex-1 max-w-56 bg-gradient-to-r from-[#c9a24b]/60 to-transparent" />
                    </div>
                    <div className="flex justify-center mt-2">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-44 opacity-90" />
                    </div>
                </Reveal>

                {/* Nível 1 — Patrocinadora Exclusiva + Parceiro Oficial, lado a lado */}
                <Reveal delay={0.06}>
                    <div className="mt-10 flex flex-wrap items-end justify-center gap-x-14 gap-y-8" data-testid="patrocinador-exclusiva">
                        {TOPO.map((p) => (
                            <div key={p.id} className="flex flex-col items-center gap-4" data-testid={`patrocinador-topo-${p.id}`}>
                                <LogoWrap p={p} testid={`patrocinador-topo-${p.id}-link`}>
                                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                                        className={`${p.tier === "exclusiva" ? "h-44 sm:h-56" : "h-36 sm:h-44"} w-auto object-contain drop-shadow-[0_0_34px_rgba(201,162,75,0.35)] group-hover:scale-[1.03] transition-transform duration-500`} data-testid={`patrocinador-topo-${p.id}-logo`} />
                                </LogoWrap>
                                <p className="font-caps text-[11px] sm:text-xs tracking-[0.36em] uppercase gold-text" data-testid={`patrocinador-topo-${p.id}-caption`}>
                                    {tiers[p.tier]}
                                </p>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* Nível 2 — Premium */}
                <Reveal delay={0.1}>
                    <div className="mt-12 flex flex-col items-center gap-5" data-testid="patrocinadores-premium">
                        <TierLabel testid="tier-premium-label">{tiers.premium}</TierLabel>
                        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
                            {porTier("premium").map((p) => (
                                <LogoWrap key={p.id} p={p} testid={`patrocinador-premium-${p.id}`}>
                                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                                        className={`${logoCls("h-24 sm:h-32")} drop-shadow-[0_0_28px_rgba(201,162,75,0.25)]`} data-testid={`patrocinador-premium-${p.id}-logo`} />
                                </LogoWrap>
                            ))}
                        </div>
                    </div>
                </Reveal>

                {/* Nível 3 — Intermediários */}
                <Reveal delay={0.14}>
                    <div className="mt-12 flex flex-col items-center gap-5" data-testid="patrocinadores-intermediario">
                        <TierLabel testid="tier-intermediario-label">{tiers.intermediario}</TierLabel>
                        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5">
                            {porTier("intermediario").map((p) => (
                                <LogoWrap key={p.id} p={p} testid={`patrocinador-intermediario-${p.id}`}>
                                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                                        className={logoCls("h-16 sm:h-20")} data-testid={`sponsor-img-${p.id}`} />
                                </LogoWrap>
                            ))}
                        </div>
                    </div>
                </Reveal>

                {/* Nível 4 — Apoiantes */}
                <Reveal delay={0.18}>
                    <div className="mt-10 flex flex-col items-center gap-4" data-testid="patrocinadores-apoiante">
                        <TierLabel testid="tier-apoiante-label">{tiers.apoiante}</TierLabel>
                        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
                            {porTier("apoiante").map((p) => (
                                <LogoWrap key={p.id} p={p} testid={`patrocinador-apoiante-${p.id}`}>
                                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                                        className={logoCls("h-12 sm:h-14")} data-testid={`sponsor-img-${p.id}`} />
                                </LogoWrap>
                            ))}
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-10 flex justify-center">
                        <Link
                            to="/patrocinadores"
                            data-testid="mais-patrocinadores-button"
                            className="group flex items-center gap-2 border border-[#c9a24b]/60 px-5 py-2.5 hover:border-[#c9a24b] hover:bg-[#c9a24b]/5 transition-colors duration-400"
                        >
                            <span className="font-caps text-[10px] tracking-[0.22em] uppercase text-[#c9a24b] leading-relaxed text-center">
                                {t.patrocinadores.maisA} {t.patrocinadores.maisB}
                            </span>
                            <ChevronRight size={15} className="text-[#c9a24b] group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
